import { useDebouncedCallback, Options } from 'use-debounce'
import { EffectCallback, useEffect, useMemo } from 'react'

/**
 * Makes the given callback debounced, then calls it in a useEffect hook
 *
 * @param callback the code to run
 * @param delay delay for the debounce
 * @param deps deps passed to useEffect
 */
export function useDebouncedEffect(callback: EffectCallback, delay: number, deps: unknown[], options?: Options) {
  const memoizedOptions = useMemo(() => {
    if (!options || options.maxWait === undefined) {
      return {
        // prevent infinitely waiting in case deps change at every render due to poorly memoized deps or similar bugs
        maxWait: Math.max(1000, delay * 2),
        ...options,
      }
    }

    return options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const debouncedCallback = useDebouncedCallback(callback, delay, memoizedOptions)
  useEffect(debouncedCallback, deps)
  return debouncedCallback
}
